import { lazy, ComponentType } from 'react'

/**
 * Utility for creating lazy-loaded components with better error handling
 */
export function createLazyComponent<T extends ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    fallback?: React.ComponentType
) {
    const LazyComponent = lazy(importFn)

        // Add display name for debugging
        ; (LazyComponent as any).displayName = `Lazy(${importFn.toString().match(/\/([^/]+)\.tsx?/)?.[1] || 'Component'})`

    return LazyComponent
}

/**
 * Preload a component for better performance
 */
export function preloadComponent(importFn: () => Promise<any>) {
    const componentImport = importFn()
    return componentImport
}

/**
 * Performance monitoring utilities
 */
export const performance = {
    /**
     * Mark the start of a performance measurement
     */
    mark: (name: string) => {
        if (typeof window !== 'undefined' && window.performance) {
            window.performance.mark(name)
        }
    },

    /**
     * Measure the time between two marks
     */
    measure: (name: string, startMark: string, endMark?: string) => {
        if (typeof window !== 'undefined' && window.performance) {
            window.performance.measure(name, startMark, endMark)
            const measure = window.performance.getEntriesByName(name)[0]
            return measure?.duration
        }
        return 0
    },

    /**
     * Get Core Web Vitals
     */
    getCLS: () => {
        return new Promise((resolve) => {
            if (typeof window === 'undefined') {
                resolve(0)
                return
            }

            // Use web-vitals library if available, otherwise basic implementation
            let cls = 0
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    if (!(entry as any).hadRecentInput) {
                        cls += (entry as any).value
                    }
                }
            })

            try {
                observer.observe({ type: 'layout-shift', buffered: true })
                setTimeout(() => {
                    observer.disconnect()
                    resolve(cls)
                }, 5000)
            } catch {
                resolve(0)
            }
        })
    },

    /**
     * Get Largest Contentful Paint
     */
    getLCP: () => {
        return new Promise((resolve) => {
            if (typeof window === 'undefined') {
                resolve(0)
                return
            }

            let lcp = 0
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries()
                const lastEntry = entries[entries.length - 1]
                lcp = lastEntry.startTime
            })

            try {
                observer.observe({ type: 'largest-contentful-paint', buffered: true })
                setTimeout(() => {
                    observer.disconnect()
                    resolve(lcp)
                }, 5000)
            } catch {
                resolve(0)
            }
        })
    },

    /**
     * Get First Input Delay
     */
    getFID: () => {
        return new Promise((resolve) => {
            if (typeof window === 'undefined') {
                resolve(0)
                return
            }

            let fid = 0
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries()) {
                    fid = (entry as any).processingStart - entry.startTime
                }
            })

            try {
                observer.observe({ type: 'first-input', buffered: true })
                setTimeout(() => {
                    observer.disconnect()
                    resolve(fid)
                }, 10000)
            } catch {
                resolve(0)
            }
        })
    }
}

/**
 * Resource hints for preloading critical resources
 */
export const resourceHints = {
    /**
     * Preload a resource
     */
    preload: (href: string, as: string, type?: string) => {
        if (typeof document === 'undefined') return

        const link = document.createElement('link')
        link.rel = 'preload'
        link.href = href
        link.as = as
        if (type) link.type = type
        document.head.appendChild(link)
    },

    /**
     * Prefetch a resource
     */
    prefetch: (href: string) => {
        if (typeof document === 'undefined') return

        const link = document.createElement('link')
        link.rel = 'prefetch'
        link.href = href
        document.head.appendChild(link)
    },

    /**
     * Preconnect to a domain
     */
    preconnect: (href: string, crossorigin?: boolean) => {
        if (typeof document === 'undefined') return

        const link = document.createElement('link')
        link.rel = 'preconnect'
        link.href = href
        if (crossorigin) link.crossOrigin = 'anonymous'
        document.head.appendChild(link)
    }
}

/**
 * Intersection Observer utility for lazy loading
 */
export function createIntersectionObserver(
    callback: IntersectionObserverCallback,
    options?: IntersectionObserverInit
) {
    if (typeof window === 'undefined') return null

    const defaultOptions: IntersectionObserverInit = {
        root: null,
        rootMargin: '50px',
        threshold: 0.1,
        ...options
    }

    return new IntersectionObserver(callback, defaultOptions)
}

/**
 * Debounce utility for performance optimization
 */
export function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number,
    immediate?: boolean
): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout | null = null

    return function executedFunction(...args: Parameters<T>) {
        const later = () => {
            timeout = null
            if (!immediate) func(...args)
        }

        const callNow = immediate && !timeout

        if (timeout) clearTimeout(timeout)
        timeout = setTimeout(later, wait)

        if (callNow) func(...args)
    }
}

/**
 * Throttle utility for performance optimization
 */
export function throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
): (...args: Parameters<T>) => void {
    let inThrottle: boolean = false

    return function executedFunction(...args: Parameters<T>) {
        if (!inThrottle) {
            func(...args)
            inThrottle = true
            setTimeout(() => inThrottle = false, limit)
        }
    }
}